import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/common/ui/Form/Button'
import { TokenAmount } from '@/common/ui/TokenAmount'
import { cn } from '@/common/lib/cn/cn'
import { Icon } from '@/common/ui/Icon/Icon'
import { resolveRelativeAssetUrl } from '@/common/lib/assetsUrl'
import { getMarketplaceExamplesApiV1MarketplaceExamplesGet } from '@/common/api/client/sdk.gen'
import { useProductAutoPipeline } from '@/app/entities/product/model'

type ProductAutoPipelineChooserProps = {
    modalId?: string
}

const PIPELINE_TOKEN_COST = 170

export const ProductAutoPipelineChooser = ({ modalId = 'create-product-modal' }: ProductAutoPipelineChooserProps) => {
    const { handleRunPipeline, isPending, stepLabel } = useProductAutoPipeline(modalId)
    const [selectedProductId, setSelectedProductId] = useState<string | null>(null)

    const marketplaceExamplesQuery = useQuery({
        queryKey: ['marketplace-examples', 'auto-pipeline'],
        queryFn: async () => {
            const response = await getMarketplaceExamplesApiV1MarketplaceExamplesGet({
                query: { page: 1, size: 12 },
            })
            return response.data?.items ?? []
        },
    })

    const products = marketplaceExamplesQuery.data ?? []

    if (!marketplaceExamplesQuery.isLoading && products.length === 0) {
        return null
    }

    const selectedProduct = products.find(product => product.id === selectedProductId)
    const canLaunch = Boolean(selectedProduct) && !isPending
    const launchLabel = isPending && stepLabel ? stepLabel : 'Запустить'

    const handleSelectProduct = (productId: string) => {
        if (isPending) {
            return
        }
        setSelectedProductId(productId)
    }

    const handleLaunch = () => {
        if (!selectedProduct || isPending) {
            return
        }
        handleRunPipeline(selectedProduct.url)
    }

    return (
        <div className='flex flex-col gap-4 border-t border-[rgba(255,255,255,0.08)] pt-6'>
            <section className='flex flex-col gap-4' aria-labelledby='product-auto-pipeline-intro-title'>
                <div className='flex flex-col gap-2'>
                    <p id='product-auto-pipeline-intro-title' className='text-base font-medium text-white'>
                        Знакомство с системой
                    </p>
                    <p className='text-sm leading-relaxed text-[var(--gray-2,#aba7af)]'>
                        Чтобы проверить систему, выберите один из предложенных товаров. Сервис сделает аналитику,
                        сгенерирует описание карточки и создаст изображения автоматически.
                    </p>
                </div>

                {marketplaceExamplesQuery.isLoading ? (
                    <div className='flex min-h-[88px] items-center justify-center rounded-xl bg-[rgba(255,255,255,0.02)] px-3 py-4'>
                        <span className='text-xs text-[var(--gray-2,#aba7af)]'>Загрузка примеров товаров…</span>
                    </div>
                ) : (
                    <div
                        className='flex flex-wrap justify-center gap-2'
                        role='listbox'
                        aria-label='Товары для знакомства с системой'
                    >
                        {products.map(product => {
                            const isSelected = selectedProductId === product.id
                            const productImageUrl = resolveRelativeAssetUrl(product.image_url)
                            const productSubtitle = product.marketplace === 'ozon' ? 'Ozon' : product.marketplace

                            return (
                                <button
                                    key={product.id}
                                    type='button'
                                    role='option'
                                    aria-selected={isSelected}
                                    disabled={isPending}
                                    onClick={() => handleSelectProduct(product.id)}
                                    className={cn(
                                        'group relative flex h-[260px] w-full max-w-[196px] min-w-[132px] flex-1 cursor-pointer overflow-hidden rounded-xl border text-left transition-colors',
                                        isSelected
                                            ? 'border-[#be96ff] bg-[rgba(190,150,255,0.12)]'
                                            : 'border-transparent bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(255,255,255,0.06)]',
                                        isPending && 'cursor-not-allowed opacity-60'
                                    )}
                                    aria-label={`Выбрать товар: ${product.name}`}
                                >
                                    <div className='absolute inset-0 bg-[#27272E]' aria-hidden>
                                        {productImageUrl ? (
                                            <img src={productImageUrl} alt='' className='h-full w-full object-cover' />
                                        ) : (
                                            <div className='flex h-full w-full items-center justify-center text-[10px] text-[var(--gray-2,#aba7af)]'>
                                                нет фото
                                            </div>
                                        )}
                                    </div>

                                    <div
                                        className={cn(
                                            'pointer-events-none absolute inset-0 transition-colors group-hover:bg-[rgba(190,150,255,0.07)]',
                                            isSelected && 'bg-[rgba(190,150,255,0.1)]'
                                        )}
                                        aria-hidden
                                    />

                                    <div className='relative z-10 flex-1 mt-auto bg-gradient-to-t from-[rgba(25,15,27,0.96)] via-[rgba(25,15,27,0.82)] to-transparent px-2 pb-2 pt-7'>
                                        <p className='text-sm font-medium text-white'>{product.name}</p>
                                        <div className='mt-1 flex items-center gap-1 text-[var(--gray-2,#aba7af)]'>
                                            <span title={productSubtitle}>
                                                {product.marketplace === 'wildberries' ? (
                                                    <Icon name='wb' />
                                                ) : (
                                                    <Icon name='ozon' />
                                                )}
                                            </span>
                                            <span className='truncate'>{productSubtitle}</span>
                                        </div>
                                    </div>
                                </button>
                            )
                        })}
                    </div>
                )}

                <div className='flex items-center justify-end gap-2 w-full'>
                    <Button
                        as='label'
                        htmlFor={modalId}
                        size='lg'
                        emphasis='accent'
                        variant='ghost'
                        className={cn('px-4', isPending && 'pointer-events-none opacity-50')}
                        aria-label='Отменить добавление товара'
                    >
                        Отмена
                    </Button>
                    <Button
                        type='button'
                        size='lg'
                        emphasis='accent'
                        className='px-6'
                        disabled={!canLaunch}
                        loading={isPending}
                        onClick={handleLaunch}
                        aria-label='Запустить автогенерацию выбранного товара'
                    >
                        <span>{launchLabel}</span>
                        {!isPending && <TokenAmount value={PIPELINE_TOKEN_COST} />}
                    </Button>
                </div>
            </section>
        </div>
    )
}
